/**
 * MCP Live Feed Component
 * Real-time visualization of MCP tool calls with Brain cache impact
 */

import React, { useState, useEffect } from 'react';
import { useDashboardStore } from '../store/dashboardStore';
import { Activity, Zap, TrendingUp } from 'lucide-react';

interface ToolMetrics {
  name: string;
  calls: number;
  hits: number;
  tokensSaved: number;
  avgLatency: number;
}

export const McpLiveFeed: React.FC = () => {
  const events = useDashboardStore((state) => state.events);
  const metrics = useDashboardStore((state) => state.metrics);
  const [toolMetrics, setToolMetrics] = useState<ToolMetrics[]>([]);
  const [hitRate, setHitRate] = useState(0);

  // Calculate metrics from events
  useEffect(() => {
    const mcpEvents = events.filter((e) => e.type === 'mcp_tool_call' || e.type === 'brain_hit' || e.type === 'brain_miss');
    
    // Group by tool
    const toolMap: Record<string, ToolMetrics> = {};
    const brainHits = mcpEvents.filter((e) => e.type === 'brain_hit' || (e.tokensImpact && e.tokensImpact > 0)).length;
    const brainMisses = mcpEvents.filter((e) => e.type === 'brain_miss' || (!e.tokensImpact || e.tokensImpact === 0)).length;
    
    mcpEvents.forEach((e: any) => {
      const toolName = e.tool || (e.type === 'brain_hit' ? 'Brain Hit' : e.type === 'brain_miss' ? 'Brain Miss' : 'Unknown');
      if (!toolMap[toolName]) {
        toolMap[toolName] = {
          name: toolName,
          calls: 0,
          hits: 0,
          tokensSaved: 0,
          avgLatency: 0,
        };
      }
      toolMap[toolName].calls++;
      if (e.type === 'brain_hit' || (e.tokensImpact && e.tokensImpact > 0)) toolMap[toolName].hits++;
      toolMap[toolName].tokensSaved += (e.tokensImpact || e.tokensSaved || 0);
      toolMap[toolName].avgLatency = (toolMap[toolName].avgLatency * (toolMap[toolName].calls - 1) + (e.latency || 0)) / toolMap[toolName].calls;
    });

    setToolMetrics(Object.values(toolMap).sort((a, b) => b.calls - a.calls));
    
    // Calculate overall hit rate
    const total = brainHits + brainMisses;
    const rate = total > 0 ? (brainHits / total) * 100 : 0;
    setHitRate(rate);
  }, [events]);

  const totalEvents = events.filter((e) => e.type === 'mcp_tool_call' || e.type === 'brain_hit' || e.type === 'brain_miss').length;
  const totalTokensSaved = toolMetrics.reduce((sum, m) => sum + m.tokensSaved, 0);

  return (
    <div className="flex flex-col h-full bg-transparent overflow-hidden relative">
      {/* Header */}
      <div className="px-4 py-3 border-b border-white/5 bg-white/5 flex-shrink-0">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-6 h-6 bg-gradient-to-br from-purple-500 to-pink-600 rounded-lg flex items-center justify-center shadow-sm flex-shrink-0">
            <Activity className="w-3.5 h-3.5 text-white" />
          </div>
          <h2 className="text-[10px] font-black text-white uppercase tracking-widest">MCP Live Feed</h2>
          <span className="ml-auto text-[10px] font-black px-2 py-0.5 bg-purple-500/20 text-purple-400 rounded-full border border-purple-500/30">
            {totalEvents}
          </span>
        </div>

        {/* Stats Row */}
        <div className="flex items-center gap-3 flex-wrap">
          <span className="inline-flex items-center gap-1 text-[10px] font-black px-2 py-0.5 rounded-full bg-cyan-500/10 text-cyan-400 border border-cyan-500/20">
            🧠 {hitRate.toFixed(0)}% Brain Hit Rate
          </span>
          <span className="inline-flex items-center gap-1 text-[10px] font-black px-2 py-0.5 rounded-full bg-violet-500/10 text-violet-400 border border-violet-500/20">
            ⚡ {totalTokensSaved.toLocaleString()} tokens saved
          </span>
        </div>
      </div>

      {/* Tools Metrics */}
      <div className="flex-1 overflow-y-auto min-h-0">
        <div className="space-y-2 p-4">
          {toolMetrics.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center py-12">
              <Activity className="w-8 h-8 text-slate-700 mb-2" />
              <p className="text-white font-bold text-sm">No MCP activity yet</p>
              <p className="text-slate-500 text-[10px] uppercase font-bold tracking-widest mt-2">
                Tools will appear here as they execute
              </p>
            </div>
          ) : (
            toolMetrics.map((metric) => (
              <div
                key={metric.name}
                className="p-3 bg-white/5 hover:bg-white/10 border border-white/5 rounded-lg transition-all"
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[10px] font-black uppercase tracking-widest text-white">
                    {metric.name}
                  </span>
                  <span className="text-[10px] text-slate-500 font-mono font-bold">
                    {metric.avgLatency.toFixed(0)}ms
                  </span>
                </div>

                {/* Progress bars */}
                <div className="flex items-center gap-2 mb-2">
                  <div className="flex-1 h-1 bg-black/30 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-cyan-500 to-cyan-400"
                      style={{ width: `${metric.hits > 0 ? (metric.hits / metric.calls) * 100 : 0}%` }}
                    />
                  </div>
                  <span className="text-[9px] text-slate-500 font-bold w-12 text-right">
                    {metric.hits}/{metric.calls}
                  </span>
                </div>

                {/* Token impact */}
                {metric.tokensSaved > 0 && (
                  <div className="flex items-center gap-1">
                    <Zap className="w-3 h-3 text-violet-400" />
                    <span className="text-[9px] font-black text-violet-400">
                      {metric.tokensSaved.toLocaleString()} tokens saved
                    </span>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>

      {/* Pro Tier Overlay (Free Tier Only) */}
      {metrics && metrics.tier !== 'pro' && (
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-slate-900/40 to-slate-900/80 backdrop-blur-sm flex items-center justify-center rounded-2xl border border-white/5">
          <div className="text-center max-w-xs px-6">
            <TrendingUp className="w-12 h-12 text-purple-500 mx-auto mb-3" />
            <h3 className="text-sm font-black text-white mb-2">Upgrade to Pro</h3>
            <p className="text-[10px] text-slate-300 mb-4">
              Unlock semantic cache to see brain hit rates and save 60-80% on token costs with intelligent memory.
            </p>
            <a
              href="https://lemma.nxus.studio/upgrade"
              target="_blank"
              rel="noopener noreferrer"
              className="block w-full px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white text-[10px] font-black uppercase rounded-lg hover:from-purple-500 hover:to-pink-500 transition-all text-center"
            >
              Upgrade Now
            </a>
          </div>
        </div>
      )}
    </div>
  );
};
